/**
 * The Academy lesson briefing. It combines the exercise card with authority-issued airport charts,
 * while keeping chart lookup and path construction in one place.
 */
import type { AirportData } from '@atc/core';
import { COMMAND_CATALOG } from '@atc/sim';
import { airportEntry } from '../airports';
import type { Lesson, LessonTier } from './lesson';

export const TIER_LABEL: Record<LessonTier, string> = { beginner: 'Beginner', mid: 'Intermediate', pro: 'Advanced' };

export interface BriefPhrase { title: string; spoken: string; syntax: string }
export interface BriefChartDocument { label: string; relativePath: string; url: string }
export interface BriefChart {
  id: string;
  title: string;
  category: 'aerodrome' | 'sid' | 'star' | 'approach' | 'other';
  procedureNames: string[];
  runways: string[];
  airac?: number;
  effectiveFrom?: string;
  documents: BriefChartDocument[];
}

interface RawChartDocument { label?: unknown; relativePath?: unknown; path?: unknown }
interface RawChart {
  id?: unknown; title?: unknown; category?: unknown; procedureNames?: unknown; runways?: unknown;
  airac?: unknown; effectiveFrom?: unknown; effectiveDate?: unknown; relativePath?: unknown;
  path?: unknown; documents?: unknown;
}

/** The single compatibility boundary between AirportData and Academy chart presentation. */
export function airportBriefCharts(airport: AirportData): BriefChart[] {
  const shaped = airport as AirportData & { charts?: unknown; xAtc: AirportData['xAtc'] & { charts?: unknown } };
  const nested = shaped.xAtc?.charts;
  const raw = Array.isArray(shaped.charts) ? shaped.charts
    : Array.isArray(nested) ? nested
    : nested && typeof nested === 'object' && Array.isArray((nested as { items?: unknown }).items) ? (nested as { items: unknown[] }).items
    : [];
  const base = `/data/${encodeURIComponent(airport.icao.toLowerCase())}/`;
  const localUrl = (path: string) => base + path.split('/').map(encodeURIComponent).join('/');
  return raw.map((value, index) => {
    const chart = value as RawChart;
    if (typeof chart.id !== 'string' || typeof chart.title !== 'string') throw new Error(`Airport chart ${index + 1} has no id or title`);
    const rawDocuments: RawChartDocument[] = Array.isArray(chart.documents)
      ? chart.documents as RawChartDocument[]
      : [{ label: chart.title, relativePath: chart.relativePath ?? chart.path }];
    const documents = rawDocuments.map((document, documentIndex) => {
      const relativePath = typeof document.relativePath === 'string' ? document.relativePath : typeof document.path === 'string' ? document.path : '';
      if (!relativePath) throw new Error(`Airport chart ${chart.id} document ${documentIndex + 1} has no local PDF path`);
      return {
        label: typeof document.label === 'string' && document.label ? document.label : rawDocuments.length > 1 ? `Page set ${documentIndex + 1}` : chart.title as string,
        relativePath,
        url: localUrl(relativePath),
      };
    });
    if (!documents.length) throw new Error(`Airport chart ${chart.id} has no PDF documents`);
    const category = chart.category === 'aerodrome' || chart.category === 'sid' || chart.category === 'star' || chart.category === 'approach' ? chart.category : 'other';
    return {
      id: chart.id,
      title: chart.title,
      category,
      procedureNames: Array.isArray(chart.procedureNames) ? chart.procedureNames.filter((v): v is string => typeof v === 'string') : [],
      runways: Array.isArray(chart.runways) ? chart.runways.filter((v): v is string => typeof v === 'string') : [],
      airac: typeof chart.airac === 'number' ? chart.airac : airport.airac,
      effectiveFrom: typeof chart.effectiveFrom === 'string' ? chart.effectiveFrom : typeof chart.effectiveDate === 'string' ? chart.effectiveDate : undefined,
      documents,
    };
  });
}

export interface LessonBrief {
  title: string;
  tier: string;
  station: string;
  situation: string;
  scene: string[];
  objectives: string[];
  notes: string[];
  phrases: BriefPhrase[];
  steps: number;
  charts: BriefChart[];
  airportCharts: BriefChart[];
}

export function lessonBrief(lesson: Lesson, airport: AirportData): LessonBrief {
  const name = airportEntry(airport.icao)?.name ?? airport.name;
  const phrases = lesson.commands
    .map((id) => COMMAND_CATALOG.find((c) => c.id === id))
    .filter((c): c is NonNullable<typeof c> => !!c)
    .map((c) => ({ title: c.title, spoken: c.spoken, syntax: c.syntax }));
  const airportCharts = airportBriefCharts(airport);
  const byId = new Map(airportCharts.map((chart) => [chart.id, chart]));
  const charts = lesson.briefing.chartIds.map((id) => {
    const chart = byId.get(id);
    if (!chart) throw new Error(`${lesson.id} names unknown airport chart ${id}`);
    return chart;
  });
  return {
    title: lesson.title,
    tier: lesson.id === 'tutorial' ? 'Tutorial' : TIER_LABEL[lesson.tier],
    station: [airport.icao.toUpperCase(), name?.toUpperCase()].filter(Boolean).join(' · '),
    situation: lesson.briefing.situation,
    scene: lesson.briefing.scene,
    objectives: lesson.briefing.objectives,
    notes: lesson.briefing.notes,
    phrases,
    steps: lesson.build(airport).steps.length,
    charts,
    airportCharts,
  };
}

export interface BriefPanelOptions {
  mode?: 'start' | 'review';
  onStart(): void;
  onExit?(): void;
  speech?: { enabled: boolean; allowed: boolean; onChange(on: boolean): void };
  playback?: { enabled: boolean; allowed: boolean; onChange(on: boolean): void };
}

const esc = (s: string) => s.replace(/[&<>\"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const CATEGORY_LABEL: Record<BriefChart['category'], string> = { aerodrome: 'Aerodrome', sid: 'Departures', star: 'Arrivals', approach: 'Approaches', other: 'Other' };

/** Renders over the display; the caller keeps the sim paused until the primary action. */
export function createLessonBriefPanel(container: HTMLElement, brief: LessonBrief, o: BriefPanelOptions): () => void {
  const review = o.mode === 'review';
  const hasLibrary = brief.airportCharts.length > 0;
  const initial = brief.charts[0] ?? null;
  const runways = [...new Set(brief.airportCharts.flatMap((chart) => chart.runways))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const groups = (Object.keys(CATEGORY_LABEL) as BriefChart['category'][]).map((category) => {
    const charts = brief.airportCharts.filter((chart) => chart.category === category);
    return charts.length ? `<section class="lesson-chart-group" data-chart-group="${category}"><h4>${CATEGORY_LABEL[category]}</h4>${charts.map((chart) => `<button type="button" data-chart-id="${esc(chart.id)}"><b>${esc(chart.title)}</b><small>${esc([...chart.procedureNames, ...chart.runways.map((r) => `RWY ${r}`)].join(' · '))}</small></button>`).join('')}</section>` : '';
  }).join('');
  const el = document.createElement('div');
  el.className = 'overlay lesson-brief-backdrop';
  el.innerHTML = `
    <section class="lesson-brief${hasLibrary ? ' has-charts' : ''}" role="dialog" aria-modal="true" aria-labelledby="lesson-brief-title">
      <header class="lesson-brief-head">
        <div>
          <p class="lesson-brief-kicker">${esc(brief.tier)} exercise · ${brief.steps} step${brief.steps === 1 ? '' : 's'}</p>
          <h2 id="lesson-brief-title">${esc(brief.title)}</h2>
          <p class="lesson-brief-station">${esc(brief.station)}</p>
        </div>
        <p class="lesson-brief-situation">${esc(brief.situation)}</p>
      </header>
      <div class="lesson-brief-content">
        <div class="lesson-brief-body">
          <section class="lesson-brief-scene" aria-label="Situation">${brief.scene.map((p) => `<p>${esc(p)}</p>`).join('')}</section>
          <section class="lesson-brief-plate" aria-label="What is expected">
            <h3>What is expected</h3>
            <ol class="lesson-brief-objectives">${brief.objectives.map((t) => `<li>${esc(t)}</li>`).join('')}</ol>
          </section>
          ${brief.notes.length ? `<ul class="lesson-brief-notes">${brief.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : ''}
          ${brief.phrases.length ? `<section class="lesson-brief-phrases" aria-label="Calls you will use">
            <h3>Calls you will use</h3>
            <dl>${brief.phrases.map((p) => `<dt>${esc(p.title)}</dt><dd>“${esc(p.spoken)}”<code>${esc(p.syntax)}</code></dd>`).join('')}</dl>
            <p class="lesson-brief-aside">${brief.tier === 'Tutorial' ? 'Use the aircraft cards for this tutorial. You can speak or type these calls in a later watch.' : 'Speak it or type it. The coach panel repeats the call for each step.'}</p>
          </section>` : ''}
        </div>
        ${hasLibrary ? `<aside class="lesson-brief-charts" aria-label="Official airport charts">
          <div class="lesson-chart-toolbar">
            <div class="lesson-chart-tabs" role="tablist" aria-label="Charts for this lesson">${brief.charts.map((chart) => `<button type="button" role="tab" data-lesson-chart="${esc(chart.id)}" aria-selected="${chart.id === initial?.id}">${esc(chart.title)}</button>`).join('')}</div>
            <button type="button" class="btn lesson-chart-library-toggle" data-act="library" aria-expanded="${initial ? 'false' : 'true'}">All airport charts <span>${brief.airportCharts.length}</span></button>
          </div>
          <div class="lesson-chart-library"${initial ? ' hidden' : ''}>
            <div class="lesson-chart-filters">
              <label><span>Find a chart</span><input type="search" data-chart-search placeholder="Procedure or chart name"></label>
              <label><span>Runway</span><select data-chart-runway><option value="">All runways</option>${runways.map((runway) => `<option value="${esc(runway.toLowerCase())}">${esc(runway)}</option>`).join('')}</select></label>
            </div>
            <div class="lesson-chart-groups">${groups}</div>
            <p class="lesson-chart-empty" hidden>No charts match this search.</p>
          </div>
          <div class="lesson-chart-viewer"${initial ? '' : ' hidden'}>
            <div class="lesson-chart-title"><div><span data-chart-category></span><h3 data-chart-title></h3><p data-chart-meta></p></div><a class="btn" data-chart-open target="_blank" rel="noopener">Open full size</a></div>
            <div class="lesson-chart-documents" role="tablist" aria-label="Chart documents"></div>
            <div class="lesson-chart-frame"></div>
          </div>
        </aside>` : ''}
      </div>
      <div class="lesson-brief-actions">
        <button type="button" class="btn-primary" data-act="start">${review ? 'Return to frequency' : 'Take the frequency'}</button>
        ${review ? '' : `<button type="button" class="btn" data-act="exit">${brief.tier === 'Tutorial' ? 'Main menu' : 'Back to Academy'}</button>`}
        ${!review && o.playback ? `<label class="lesson-brief-radio${o.playback.allowed ? '' : ' off'}">
          <input type="checkbox" data-act="playback"${o.playback.enabled ? ' checked' : ''}${o.playback.allowed ? '' : ' disabled'}>
          <span>Hear the radio<small>${o.playback.allowed ? 'Pilots and your own calls are read aloud.' : 'Turned off by the current game configuration.'}</small></span>
        </label>` : ''}
        ${!review && o.speech ? `<label class="lesson-brief-radio${o.speech.allowed ? '' : ' off'}">
          <input type="checkbox" data-act="speech"${o.speech.enabled ? ' checked' : ''}${o.speech.allowed ? '' : ' disabled'}>
          <span>Speak on the radio<small>${o.speech.allowed ? 'Hold Space and say the call instead of typing it. Uses AI: your audio goes to OpenAI only while you transmit.' : 'Turned off by the current game configuration.'}</small></span>
        </label>` : ''}
      </div>
      ${review ? '' : '<p class="lesson-brief-ai" data-brief-ai aria-live="polite"></p>'}
    </section>`;
  container.appendChild(el);
  const previousFocus = document.activeElement as HTMLElement | null;
  const chartById = new Map(brief.airportCharts.map((chart) => [chart.id, chart]));
  const library = el.querySelector<HTMLElement>('.lesson-chart-library');
  const viewer = el.querySelector<HTMLElement>('.lesson-chart-viewer');
  const libraryToggle = el.querySelector<HTMLButtonElement>('[data-act=library]');
  let current: BriefChart | null = initial;
  let currentDocument = 0;

  const renderViewer = () => {
    if (!current || !viewer) return;
    const chartDocument = current.documents[currentDocument] ?? current.documents[0]!;
    viewer.hidden = false;
    if (library) library.hidden = true;
    libraryToggle?.setAttribute('aria-expanded', 'false');
    el.querySelector<HTMLElement>('[data-chart-category]')!.textContent = CATEGORY_LABEL[current.category];
    el.querySelector<HTMLElement>('[data-chart-title]')!.textContent = current.title;
    const meta = [current.airac ? `AIRAC ${current.airac}` : '', current.effectiveFrom ? `effective ${current.effectiveFrom}` : '', current.runways.length ? `runways ${current.runways.join(', ')}` : ''].filter(Boolean);
    el.querySelector<HTMLElement>('[data-chart-meta]')!.textContent = meta.join(' · ');
    const open = el.querySelector<HTMLAnchorElement>('[data-chart-open]')!;
    open.href = chartDocument.url;
    open.setAttribute('aria-label', `Open ${current.title} full size`);
    const documents = el.querySelector<HTMLElement>('.lesson-chart-documents')!;
    documents.replaceChildren();
    if (current.documents.length > 1) current.documents.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.chartDocument = String(index); button.textContent = item.label;
      button.setAttribute('role', 'tab'); button.setAttribute('aria-selected', String(index === currentDocument));
      documents.appendChild(button);
    });
    const frame = el.querySelector<HTMLElement>('.lesson-chart-frame')!;
    frame.replaceChildren();
    const iframe = document.createElement('iframe');
    iframe.title = `${current.title} — ${chartDocument.label}`;
    // The PDF viewer has its own tab order, which can strand focus inside this modal.
    // The adjacent full-size link remains keyboard accessible.
    iframe.tabIndex = -1;
    iframe.src = chartDocument.url;
    frame.appendChild(iframe);
    el.querySelectorAll<HTMLElement>('[data-lesson-chart]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.lessonChart === current?.id)));
  };
  if (current) renderViewer();

  const filterLibrary = () => {
    const query = (el.querySelector<HTMLInputElement>('[data-chart-search]')?.value ?? '').trim().toLowerCase();
    const runway = el.querySelector<HTMLSelectElement>('[data-chart-runway]')?.value ?? '';
    let visible = 0;
    for (const button of el.querySelectorAll<HTMLButtonElement>('[data-chart-id]')) {
      const chart = chartById.get(button.dataset.chartId ?? '');
      const haystack = chart ? [chart.title, ...chart.procedureNames, ...chart.runways].join(' ').toLowerCase() : '';
      const show = !!chart && (!query || haystack.includes(query)) && (!runway || chart.runways.some((item) => item.toLowerCase() === runway));
      button.hidden = !show;
      if (show) visible++;
    }
    for (const group of el.querySelectorAll<HTMLElement>('[data-chart-group]')) group.hidden = !group.querySelector('[data-chart-id]:not([hidden])');
    const empty = el.querySelector<HTMLElement>('.lesson-chart-empty');
    if (empty) empty.hidden = visible > 0;
  };

  const dispose = () => { el.remove(); if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true }); };
  const finish = (exit: boolean) => { dispose(); if (exit) o.onExit?.(); else o.onStart(); };
  el.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (library && !library.hidden && current) { library.hidden = true; viewer!.hidden = false; libraryToggle?.setAttribute('aria-expanded', 'false'); libraryToggle?.focus(); }
      else finish(!review);
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = [...el.querySelectorAll<HTMLElement>('button:not([hidden]), a[href], input:not([disabled]), select:not([disabled]), summary, [tabindex="0"]')].filter((control) => control.offsetParent !== null);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  el.addEventListener('input', (event) => { if ((event.target as HTMLElement).matches('[data-chart-search]')) filterLibrary(); });
  el.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.act === 'speech') o.speech?.onChange(target.checked);
    if (target instanceof HTMLInputElement && target.dataset.act === 'playback') o.playback?.onChange(target.checked);
    if (target instanceof HTMLSelectElement && target.matches('[data-chart-runway]')) filterLibrary();
  });
  el.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-act], [data-chart-id], [data-lesson-chart], [data-chart-document]');
    const act = target?.dataset.act;
    if (act === 'start') finish(false);
    else if (act === 'exit') finish(true);
    else if (act === 'library' && library && viewer) {
      const open = library.hidden;
      library.hidden = !open; viewer.hidden = open;
      libraryToggle?.setAttribute('aria-expanded', String(open));
      if (open) el.querySelector<HTMLInputElement>('[data-chart-search]')?.focus();
    } else if (target?.dataset.chartId || target?.dataset.lessonChart) {
      current = chartById.get(target.dataset.chartId ?? target.dataset.lessonChart ?? '') ?? current;
      currentDocument = 0; renderViewer();
    } else if (target?.dataset.chartDocument) {
      currentDocument = Number(target.dataset.chartDocument); renderViewer();
    }
  });
  el.querySelector<HTMLElement>('[data-act=start]')?.focus();
  return dispose;
}
